import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { Button, Chip, Box, Typography, Stack, Icons } from '@curves/ui-base';
import { TabsCardLayout } from '@curves/ui-shared';
import useTabState from "@util/hook/useTabState";
import useClubPurchaseReceiptApi from "@apis/useClubPurchaseReceiptApi";
import { isEqual } from "@util/utils";
import { CLUB_INVENTORY_ROUTER } from "@clubInventory/router/routerPath";
import { CLUB_PURCHASE_RECEIPT_STATUS } from "@clubInventory/constants/dataEnums";
import { PurchaseOrderSummaryCard, PurchaseOrderProductList, PurchaseOrderStockInRecords } from "@clubInventory/purchase/components/";
import PurchaseDialogBox from "@clubInventory/purchase/dialog/PurchaseDialogBox";
import { statusColorByValue } from "@clubInventory/purchase/PurchaseOverviewPage.config";

// 功能：進貨單明細頁籤定義
const TAB_VALUES = {
	PRODUCT_LIST: "product-list",
	STOCK_IN_RECORDS: "stock-in-records",
};

// 進貨單明細頁籤定義
const TABS = [
	{ label: "商品清單", value: TAB_VALUES.PRODUCT_LIST },
	{ label: "入庫紀錄", value: TAB_VALUES.STOCK_IN_RECORDS },
];

// 功能：進貨單明細（狀態、摘要、商品清單/入庫紀錄、結單/驗收操作）
const PurchaseOrderPage = () => {
	const { purchaseReceiptID } = useParams();
	const dialogBoxRef = useRef(null);
	const { getClubPurchaseReceiptApi, getClubPurchaseReceiptInventoryRecordsApi } = useClubPurchaseReceiptApi();
	const [purchaseReceipt, setPurchaseReceipt] = useState(null);
	const [stockInRecords, setStockInRecords] = useState([]);
	const { activeTab: tabValue, handleTabChange } = useTabState(TAB_VALUES.PRODUCT_LIST);

	// fetch 進貨單 API
	const fetchReceipt = useCallback(
		async receiptID => {
			const resp = await getClubPurchaseReceiptApi(receiptID);
			if (resp) setPurchaseReceipt(resp);
		},
		// eslint-disable-next-line
		[getClubPurchaseReceiptApi],
	);

	// fetch 入庫紀錄 API
	const fetchStockInRecords = useCallback(
		async id => {
			const resp = await getClubPurchaseReceiptInventoryRecordsApi(id);
			if (resp) setStockInRecords(resp);
		},
		// eslint-disable-next-line
		[getClubPurchaseReceiptInventoryRecordsApi],
	);

	// 提供給 dialog 在操作完成後刷新頁面資料
	const refreshReceipt = useCallback(
		() => {
			if (purchaseReceiptID) fetchReceipt(purchaseReceiptID);
			if (purchaseReceiptID) fetchStockInRecords(purchaseReceiptID);
		},
		// eslint-disable-next-line
		[purchaseReceiptID, fetchReceipt],
	);

	// 只有在待驗收或部分驗收狀態才顯示結單、開始驗收按鈕
	const showOrderActions =
		purchaseReceipt && (purchaseReceipt.purchaseStatus === CLUB_PURCHASE_RECEIPT_STATUS.WAITTINSPECTION || purchaseReceipt.purchaseStatus === CLUB_PURCHASE_RECEIPT_STATUS.PARTIALINSPECTION);

	// TODO: 後續可優化為將 actions 定義為 function，並在需要時才呼叫，而不是每次 render 都建立一組新的 actions 陣列
	const actions = useMemo(() => {
		if (!showOrderActions || !purchaseReceipt) return undefined;
		const isPending = isEqual(purchaseReceipt.purchaseStatus, CLUB_PURCHASE_RECEIPT_STATUS.WAITTINSPECTION);
		return [
			{
				label: "結單",
				variant: "outlined",
				color: "error",
				onClick: () => {
					const { openPurchaseOrderCloseDialog } = dialogBoxRef.current ?? {};
					if (typeof openPurchaseOrderCloseDialog === "function") {
						openPurchaseOrderCloseDialog(purchaseReceipt, refreshReceipt);
					}
				},
			},
			{
				label: isPending ? "開始驗收" : "繼續驗收",
				variant: "contained",
				color: isPending ? "secondary" : "primary",
				startIcon: <Icons.CarrierCode />,
				onClick: () => {
					const { openPurchaseInspectionAndStorageDialog } = dialogBoxRef.current ?? {};
					if (typeof openPurchaseInspectionAndStorageDialog === "function") {
						openPurchaseInspectionAndStorageDialog({ clubPurchaseReceiptID: purchaseReceiptID }, refreshReceipt);
					}
				},
			},
		];
	}, [showOrderActions, purchaseReceipt, purchaseReceiptID, refreshReceipt]);

	// 初始載入或切換 Tab 時 fetch 資料
	useEffect(
		() => {
			if (!purchaseReceiptID) return;

			if (isEqual(tabValue, TAB_VALUES.STOCK_IN_RECORDS)) {
				fetchStockInRecords(purchaseReceiptID);
			} else if (isEqual(tabValue, TAB_VALUES.PRODUCT_LIST)) {
				fetchReceipt(purchaseReceiptID);
			}
		},
		// eslint-disable-next-line
		[tabValue, purchaseReceiptID, fetchStockInRecords, fetchReceipt],
	);

	return (
		<Box className="px-3 py-2">
			<Stack direction="row" justifyContent="space-between" alignItems="center" className="mb-2">
				<Stack direction="row" alignItems="center" spacing={1}>
					<Typography variant="h5" className="mb-0 font-weight-bold">
						進貨單明細
					</Typography>
					<Chip label={purchaseReceipt?.purchaseStatusText} color={statusColorByValue[purchaseReceipt?.purchaseStatus]} size="small" />
				</Stack>
				<Button component={NavLink} to={CLUB_INVENTORY_ROUTER.PURCHASE.OVERVIEW} variant="contained">
					返回總覽
				</Button>
			</Stack>

			<PurchaseOrderSummaryCard className="mb-3" purchaseReceipt={purchaseReceipt} />

			<TabsCardLayout tabs={TABS} activeTab={tabValue} onTabChange={handleTabChange} actions={actions} tabsContent={{ className: "px-3 py-2" }}>
				{isEqual(tabValue, TAB_VALUES.PRODUCT_LIST) && (
					<PurchaseOrderProductList
						products={purchaseReceipt?.items || []}
						totalQuantity={purchaseReceipt?.quantity ?? 0}
						totalReceived={purchaseReceipt?.actualQuantity ?? 0}
						purchaseStatus={purchaseReceipt?.purchaseStatus}
						height="400px"
					/>
				)}
				{isEqual(tabValue, TAB_VALUES.STOCK_IN_RECORDS) && <PurchaseOrderStockInRecords records={stockInRecords} />}
			</TabsCardLayout>

			<PurchaseDialogBox ref={dialogBoxRef} />
		</Box>
	);
};

export default PurchaseOrderPage;
